/**
 * Crossing Counting Utilities
 * 
 * Functions for counting edge crossings between layers
 * and optimizing node order via local swaps.
 */

/**
 * Count edge crossings between two adjacent layers
 * @param {Array} layer1 - Upper layer node IDs
 * @param {Array} layer2 - Lower layer node IDs
 * @param {Object} graph - Dependency graph with parents/children maps
 * @returns {number} Number of edge crossings
 */
export function countCrossings(layer1, layer2, graph) {
  let crossings = 0;
  
  // Get position maps for both layers
  const pos1 = new Map();
  layer1.forEach((id, idx) => pos1.set(id, idx));
  const pos2 = new Map();
  layer2.forEach((id, idx) => pos2.set(id, idx));
  
  // Get all edges between these layers
  const edges = [];
  layer2.forEach(childId => {
    const parents = graph.parents.get(childId) || [];
    parents.forEach(parentId => {
      if (pos1.has(parentId)) {
        edges.push({ from: pos1.get(parentId), to: pos2.get(childId) });
      }
    });
  });
  
  // Count crossings: two edges cross if their endpoints are in opposite order
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 1; j < edges.length; j++) {
      const e1 = edges[i];
      const e2 = edges[j];
      if ((e1.from < e2.from && e1.to > e2.to) || (e1.from > e2.from && e1.to < e2.to)) {
        crossings++;
      }
    }
  }
  
  return crossings;
}

/**
 * Count total crossings in a layout
 * @param {Array} layers - Array of layers, each layer is array of node IDs
 * @param {Object} graph - Dependency graph
 * @returns {number} Total crossing count
 */
export function countTotalCrossings(layers, graph) {
  let total = 0;
  for (let i = 0; i < layers.length - 1; i++) {
    total += countCrossings(layers[i], layers[i + 1], graph);
  }
  return total;
}

/**
 * Try swapping adjacent nodes in a layer to reduce crossings
 * @param {Array} layer - Layer to optimize
 * @param {Array} adjacentLayer - Adjacent layer for crossing calculation
 * @param {Object} graph - Dependency graph
 * @returns {Array} Optimized layer order
 */
export function tryLocalSwaps(layer, adjacentLayer, graph) {
  if (layer.length < 2) return layer;
  
  let improved = true;
  let result = [...layer];
  let iterations = 0;
  const maxIterations = layer.length * 2;
  
  while (improved && iterations < maxIterations) {
    improved = false;
    iterations++;
    
    for (let i = 0; i < result.length - 1; i++) {
      const currentCrossings = countCrossings(adjacentLayer, result, graph);
      
      const swapped = [...result];
      [swapped[i], swapped[i + 1]] = [swapped[i + 1], swapped[i]];
      
      const swappedCrossings = countCrossings(adjacentLayer, swapped, graph);
      
      if (swappedCrossings < currentCrossings) {
        result = swapped;
        improved = true;
      }
    }
  }
  
  return result;
}
